/**
 * `capacity` template — a compact rendering of the pick-face ceiling chart, computed with the
 * SAME math the capacity screen uses (`heldAt`, `defaultRoll`, `leverPool`, …) so a pinned
 * scenario never drifts from what `/capacity` would show for the same inputs.
 *
 * Trimmed from the interactive screen: freed slots are the SUM of each active space lever's
 * pool at `relo_stop_index` (no per-family exclusions — the spec carries none), applied as one
 * flat offset across every year rather than `capNumbers`' single-year read.
 */

import { useState } from "react";

import type { CapacityView } from "@/lib/api";
import {
  CAP_YEARS,
  defaultRoll,
  heldAt,
  type LeverFamily,
  leverPool,
  type RollPlan,
  type SpaceLeverId,
  UNIT_SKU,
  useCapacity,
  useLeverFamilies,
} from "@/lib/data";

import { AXIS, AXIS_LABEL, niceTicks } from "./axis";
import { AxisTitle } from "./axis-title";
import { ChartTooltip } from "./chart-tooltip";
import { GRID_LINE, HIGHLIGHT_PRIMARY, INK, NEUTRAL, RED_INK } from "./colors";
import { useChartSize } from "./use-chart-size";
import type { ViewChrome, ViewSize } from "./view-shell";

/** `CapacityView.roll` carries JSON object keys, i.e. string years — parse back to the served
 *  `RollPlan` shape (`Record<string, Record<number, number>>`). */
function parseRoll(roll: CapacityView["roll"]): RollPlan | undefined {
  if (!roll) {
    return undefined;
  }
  const out: RollPlan = {};
  for (const [famId, byYear] of Object.entries(roll)) {
    const inc: Record<number, number> = {};
    for (const [yr, v] of Object.entries(byYear)) {
      inc[Number(yr)] = v;
    }
    out[famId] = inc;
  }
  return out;
}

interface CapacityChartProps {
  spec: CapacityView;
  size: ViewSize;
  chrome?: ViewChrome;
}

const SPACE_LEVERS: readonly SpaceLeverId[] = ["relo", "conv", "pack"];

function freedSlots(
  spec: CapacityView,
  byLever: Record<SpaceLeverId, readonly LeverFamily[]>,
): number {
  const active = spec.active_levers ?? [];
  const reloStopIndex = spec.relo_stop_index ?? 0;
  let freed = 0;
  for (const lever of SPACE_LEVERS) {
    if (active.includes(lever)) {
      freed += leverPool(byLever[lever], reloStopIndex);
    }
  }
  return freed;
}

export function CapacityChart({ spec, size, chrome = "full" }: CapacityChartProps) {
  const capacity = useCapacity();
  const relo = useLeverFamilies("relo");
  const conv = useLeverFamilies("conv");
  const pack = useLeverFamilies("pack");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const box = useChartSize();

  if (!capacity.data) {
    return <p className="text-muted-foreground text-xs">Loading capacity model…</p>;
  }

  const model = capacity.data;
  const years = model.years?.length ? model.years : CAP_YEARS;
  const roll = parseRoll(spec.roll) ?? defaultRoll(model.npiFamilies);
  const byLever: Record<SpaceLeverId, readonly LeverFamily[]> = {
    relo: relo.data ?? [],
    conv: conv.data ?? [],
    pack: pack.data ?? [],
  };
  const freed = freedSlots(spec, byLever);

  const activeLevers = spec.active_levers ?? [];
  const base = spec.ceiling ?? model.capd.ceil;
  const ceiling = base + (activeLevers.includes("cap") ? (spec.units ?? 0) * UNIT_SKU : 0);

  const points = years.map((year) => ({
    year,
    held: Math.max(0, heldAt(roll, year, model) - freed),
  }));
  const maxV = Math.max(ceiling, ...points.map((p) => p.held)) * 1.05 || 1;
  const isCard = size === "card";
  const yTicks = niceTicks(maxV, isCard ? 3 : 5).filter((t) => t <= maxV);

  // Drawn at the box's own pixel size — see `useChartSize`.
  const w = box.width;
  const h = box.height;
  const metrics = AXIS[size];
  const pad = { l: metrics.padL, r: metrics.padR, t: metrics.padT, b: metrics.padB };
  const innerW = Math.max(1, w - pad.l - pad.r);
  const innerH = Math.max(1, h - pad.t - pad.b);
  const colW = innerW / points.length;
  const py = (v: number) => pad.t + innerH - (v / maxV) * innerH;
  const fontSize = metrics.font;
  // A year label per bar only fits while the bars are wide enough to hold one.
  const yearLabelStride = Math.max(1, Math.ceil((fontSize * 3.2) / colW));

  const hovered = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div className="relative flex h-full min-h-[100px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="min-h-0 min-w-0 flex-1">
        {w > 0 && h > 0 ? (
          <svg width={w} height={h}>
            {/* y axis: line, ticks, title */}
            <line x1={pad.l} x2={pad.l} y1={pad.t} y2={py(0)} stroke={INK} strokeWidth={1} />
            {yTicks.map((t) => (
              <g key={t}>
                <line
                  x1={pad.l - 3}
                  x2={w - pad.r}
                  y1={py(t)}
                  y2={py(t)}
                  stroke={t === 0 ? INK : GRID_LINE}
                  strokeWidth={1}
                />
                <text
                  x={pad.l - 6}
                  y={py(t) + fontSize / 3}
                  fontSize={fontSize}
                  textAnchor="end"
                  fill={AXIS_LABEL}
                >
                  {t.toLocaleString()}
                </text>
              </g>
            ))}
            <AxisTitle
              text="SKU slots held"
              x={fontSize}
              y={pad.t + innerH / 2}
              fontSize={fontSize}
              rotate
            />
            <line
              x1={pad.l}
              x2={w - pad.r}
              y1={py(ceiling)}
              y2={py(ceiling)}
              stroke={INK}
              strokeDasharray="4 3"
              strokeWidth={1}
            />
            <text x={pad.l + 2} y={py(ceiling) - 3} fontSize={fontSize} fill={INK}>
              ceiling {ceiling.toLocaleString()}
            </text>
            {points.map((p, i) => {
              const x = pad.l + i * colW + colW * 0.15;
              const barW = colW * 0.7;
              const yTop = py(p.held);
              const highlighted = spec.highlight_year === p.year;
              const over = p.held > ceiling;
              return (
                <g key={p.year}>
                  <rect
                    x={x}
                    y={yTop}
                    width={barW}
                    height={Math.max(0, py(0) - yTop)}
                    fill={highlighted ? HIGHLIGHT_PRIMARY : over ? RED_INK : NEUTRAL}
                    rx={1}
                    tabIndex={0}
                    role="img"
                    aria-label={`${p.year}: ${p.held.toLocaleString()} slots held, ceiling ${ceiling.toLocaleString()}`}
                    className="cursor-default outline-none"
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
                    onFocus={() => setHoverIndex(i)}
                    onBlur={() => setHoverIndex((cur) => (cur === i ? null : cur))}
                  />
                  {i % yearLabelStride === 0 ? (
                    <text
                      x={x + barW / 2}
                      y={h - pad.b + fontSize + 4}
                      fontSize={fontSize}
                      textAnchor="middle"
                      fill={AXIS_LABEL}
                    >
                      {p.year}
                    </text>
                  ) : null}
                </g>
              );
            })}
            <AxisTitle
              text="year"
              x={pad.l + innerW / 2}
              y={h - fontSize / 2 - 2}
              fontSize={fontSize}
            />
          </svg>
        ) : null}
      </div>
      {hovered ? (
        <ChartTooltip
          x={`${((pad.l + (hoverIndex! + 0.5) * colW) / w) * 100}%`}
          y={`${(py(hovered.held) / h) * 100}%`}
        >
          <div className="font-semibold">{hovered.year}</div>
          <div>{hovered.held.toLocaleString()} slots held</div>
          <div className={hovered.held > ceiling ? "text-red-300" : ""}>
            ceiling {ceiling.toLocaleString()}
            {hovered.held > ceiling ? " — over" : ""}
          </div>
        </ChartTooltip>
      ) : null}
      {chrome !== "bare" && freed > 0 ? (
        <p className="text-muted-foreground shrink-0 text-[11px]">
          {freed.toLocaleString()} SKU-slots freed by active levers, applied as a flat offset
        </p>
      ) : null}
    </div>
  );
}
